// Open tabs ("Offene Tabs"): every video the user opens, until they close it.
//
// - Anonymous users: localStorage only (STORAGE_KEYS.tabs), synced between browser tabs.
// - Signed-in users: the server list (/api/tabs), synced across devices. Changes are applied
//   optimistically and sent one by one; failed requests (offline, server down) stay queued in
//   localStorage and are retried on the next sync (focus, visibility, online, timer). The shown
//   list is always "last server answer + unsent changes".
// - Sign-in imports the browser-stored tabs into the account and clears them; sign-out shows
//   the (then empty) browser list again.
// - Until /api/me has answered, changes are kept aside and handed to whichever store applies.

import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { OpenTab } from '../../shared/api';
import { ApiRequestError, deleteTab, getTabs, importTabs as importTabsRequest, putTab } from './api';
import {
  STORAGE_KEYS,
  loadAccountTabs,
  loadLocalTabs,
  saveAccountTabs,
  saveLocalTabs,
  type AccountTabs,
} from './storage';
import { applyTabOp, applyTabOps, enqueueTabOp, mergedTab, removeTab, type TabOp, type TabUpdate } from './tabs';

export interface TabsApi {
  get(): Promise<OpenTab[]>;
  put(tab: OpenTab): Promise<OpenTab[]>;
  delete(videoId: string): Promise<OpenTab[]>;
  import(tabs: OpenTab[]): Promise<OpenTab[]>;
}

export interface TabsStorage {
  loadLocal(): OpenTab[];
  saveLocal(tabs: OpenTab[]): void;
  loadAccount(): AccountTabs | null;
  saveAccount(value: AccountTabs | null): void;
}

const defaultApi: TabsApi = {
  get: () => getTabs(),
  put: (tab) => putTab(tab),
  delete: (videoId) => deleteTab(videoId),
  import: (tabs) => importTabsRequest(tabs),
};

const defaultStorage: TabsStorage = {
  loadLocal: loadLocalTabs,
  saveLocal: saveLocalTabs,
  loadAccount: loadAccountTabs,
  saveAccount: saveAccountTabs,
};

/** Failures worth retrying later; anything else (bad request) drops the change. */
function isRetryable(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  return (
    error.code === 'network' ||
    error.code === 'aborted' ||
    error.code === 'login_required' ||
    error.code === 'rate_limited' ||
    (error.status ?? 500) >= 500
  );
}

const RETRY_DELAY_MS = 20_000;
/** A GET right after another one is skipped (focus and visibilitychange fire together). */
const MIN_SYNC_INTERVAL_MS = 2_000;

type Mode = { kind: 'unknown' } | { kind: 'local' } | { kind: 'account'; userId: string };

export class TabStore {
  private mode: Mode = { kind: 'unknown' };
  private local: OpenTab[];
  private account: AccountTabs | null = null;
  /** Changes made before the mode was known. */
  private early: TabOp[] = [];
  private snapshot: OpenTab[];
  private listeners = new Set<() => void>();
  private busy = false;
  private wantFetch = false;
  private lastFetch = 0;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly api: TabsApi = defaultApi,
    private readonly storage: TabsStorage = defaultStorage,
  ) {
    this.local = storage.loadLocal();
    // Best guess until /api/me answers: the account list of the last visit, else the local one.
    this.snapshot = this.compute();
  }

  // ---- React glue

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): OpenTab[] => this.snapshot;

  private compute(): OpenTab[] {
    switch (this.mode.kind) {
      case 'unknown': {
        const cached = this.storage.loadAccount();
        const base = cached ? applyTabOps(cached.tabs, cached.pending) : this.local;
        return applyTabOps(base, this.early);
      }
      case 'local':
        return this.local;
      case 'account':
        return this.account ? applyTabOps(this.account.tabs, this.account.pending) : [];
    }
  }

  private emit(): void {
    const next = this.compute();
    if (next === this.snapshot) return;
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }

  // ---- mode

  /** `null`: signed out. Call whenever /api/me has answered. */
  setUser(userId: string | null): void {
    if (userId === null) {
      if (this.mode.kind === 'local') return;
      const wasAccount = this.mode.kind === 'account';
      this.mode = { kind: 'local' };
      this.account = null;
      this.storage.saveAccount(null);
      // Signed out: the browser list again (empty after the import at sign-in).
      this.local = wasAccount ? this.storage.loadLocal() : applyTabOps(this.storage.loadLocal(), this.early);
      if (!wasAccount) this.storage.saveLocal(this.local);
      this.early = [];
      this.emit();
      return;
    }

    if (this.mode.kind === 'account' && this.mode.userId === userId) return;
    const cached = this.storage.loadAccount();
    const account: AccountTabs =
      cached && cached.userId === userId ? cached : { userId, tabs: [], pending: [] };
    // Tabs opened while signed out move into the account.
    const local = this.storage.loadLocal();
    let pending = account.pending;
    if (local.length > 0) pending = [...pending, { kind: 'import', tabs: local }];
    for (const op of this.early) pending = enqueueTabOp(pending, op);
    this.early = [];
    this.mode = { kind: 'account', userId };
    this.account = { ...account, pending };
    this.persistAccount();
    if (local.length > 0) {
      this.local = [];
      this.storage.saveLocal([]);
    }
    this.emit();
    this.sync(true);
  }

  // ---- changes

  private change(op: TabOp): void {
    switch (this.mode.kind) {
      case 'unknown':
        this.early = enqueueTabOp(this.early, op);
        break;
      case 'local':
        this.local = applyTabOp(this.local, op);
        this.storage.saveLocal(this.local);
        break;
      case 'account':
        if (!this.account) return;
        this.account = { ...this.account, pending: enqueueTabOp(this.account.pending, op, this.busy) };
        this.persistAccount();
        void this.pump();
        break;
    }
    this.emit();
  }

  /** Opens a tab or updates what is known about it (no-op when nothing changes). */
  open(update: TabUpdate): void {
    const tab = mergedTab(this.snapshot, update);
    const existing = this.snapshot.find((t) => t.videoId === update.videoId);
    if (
      existing &&
      existing.lang === tab.lang &&
      existing.title === tab.title &&
      existing.channel === tab.channel
    ) {
      return;
    }
    this.change({ kind: 'put', tab });
  }

  close(videoId: string): void {
    if (removeTab(this.snapshot, videoId) === this.snapshot) return;
    this.change({ kind: 'delete', videoId });
  }

  closeAll(): void {
    for (const tab of this.snapshot) this.change({ kind: 'delete', videoId: tab.videoId });
  }

  /** Undo: re-adds the tabs of an earlier list with their original order (the server keeps openedAt). */
  restore(tabs: OpenTab[]): void {
    const missing = tabs.filter((tab) => !this.snapshot.some((t) => t.videoId === tab.videoId));
    if (missing.length > 0) this.change({ kind: 'import', tabs: missing });
  }

  /** Another browser tab changed localStorage. */
  reloadLocal(): void {
    if (this.mode.kind === 'account') return;
    this.local = this.storage.loadLocal();
    this.emit();
  }

  // ---- server sync (signed-in users)

  /** Sends unsent changes, then fetches the current list (other devices). */
  sync(force = false): void {
    if (this.mode.kind !== 'account') return;
    if (!force && Date.now() - this.lastFetch < MIN_SYNC_INTERVAL_MS && !this.account?.pending.length) return;
    this.wantFetch = true;
    void this.pump();
  }

  private persistAccount(): void {
    this.storage.saveAccount(this.account);
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== undefined) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      this.sync(true);
    }, RETRY_DELAY_MS);
  }

  /** Processes the queue strictly in order; every answer is the full, current server list. */
  private async pump(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      while (this.mode.kind === 'account' && this.account) {
        const userId = this.account.userId;
        const op = this.account.pending[0];
        if (!op && !this.wantFetch) break;
        let tabs: OpenTab[];
        try {
          if (!op) this.wantFetch = false;
          tabs = !op
            ? await this.api.get()
            : op.kind === 'put'
              ? await this.api.put(op.tab)
              : op.kind === 'delete'
                ? await this.api.delete(op.videoId)
                : await this.api.import(op.tabs);
        } catch (error) {
          if (!this.account || this.account.userId !== userId) break;
          if (isRetryable(error)) {
            if (!op) this.wantFetch = true;
            this.scheduleRetry();
            break;
          }
          if (op) this.account = { ...this.account, pending: this.account.pending.slice(1) };
          this.persistAccount();
          this.emit();
          continue;
        }
        // Signed out or switched accounts meanwhile: drop the answer.
        if (!this.account || this.account.userId !== userId) break;
        if (!op) this.lastFetch = Date.now();
        this.account = { userId, tabs, pending: op ? this.account.pending.slice(1) : this.account.pending };
        this.persistAccount();
        this.emit();
      }
    } finally {
      this.busy = false;
    }
  }

  dispose(): void {
    if (this.retryTimer !== undefined) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
  }
}

export interface OpenTabs {
  tabs: OpenTab[];
  open(update: TabUpdate): void;
  close(videoId: string): void;
  closeAll(): void;
  restore(tabs: OpenTab[]): void;
}

/** `userId`: undefined while /api/me is loading, null when signed out. */
export function useOpenTabs(userId: string | null | undefined): OpenTabs {
  const store = useMemo(() => new TabStore(), []);
  const tabs = useSyncExternalStore(store.subscribe, store.getSnapshot);

  useEffect(() => {
    if (userId !== undefined) store.setUser(userId);
  }, [store, userId]);

  useEffect(() => {
    const sync = () => {
      if (document.visibilityState === 'visible') store.sync();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.tabs || event.key === null) store.reloadLocal();
    };
    window.addEventListener('focus', sync);
    window.addEventListener('online', sync);
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', sync);
      window.removeEventListener('online', sync);
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('storage', onStorage);
    };
  }, [store]);

  useEffect(() => () => store.dispose(), [store]);

  return useMemo(
    () => ({
      tabs,
      open: (update: TabUpdate) => store.open(update),
      close: (videoId: string) => store.close(videoId),
      closeAll: () => store.closeAll(),
      restore: (list: OpenTab[]) => store.restore(list),
    }),
    [store, tabs],
  );
}
